import { inject, Injectable, signal } from '@angular/core';
import localForage from 'localforage';
import { jwtDecode } from 'jwt-decode';
import { IHandoffSession, IPostLoginResponse, ITokenObject } from '@helipagos/auth';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { forkJoin, map, Observable, of } from 'rxjs';
import { BASE_ROUTE_NAVIGATORS } from '../providers/provide-base-route-navigator';

@Injectable({
  providedIn: 'root',
})
export class UserStoreService {
  private _redirectionUrl = signal<string | null>(null); // no need for localForage
  private navHeaderTitle = signal<string>('');

  protected nodesInjected = inject(BASE_ROUTE_NAVIGATORS);

  public baseRouteNavigators = this.nodesInjected;

  public resetRouterNavigators() {
    this.baseRouteNavigators = this.nodesInjected;
  }

  static verifyPermissionsForRoles(...labels: string[]) {
    return fromPromise(localForage.getItem<string[]>('role')).pipe(
      map((roles) => {
        if (Array.isArray(roles)) {
          return labels.some((label) => roles.includes(label));
        } else {
          return false;
        }
      })
    );
  }

  // Token

  getToken(): Observable<string | null> {
    return fromPromise(localForage.getItem('token'));
  }

  setToken(value: string) {
    return fromPromise(localForage.setItem('token', value));
  }

  isTokenExpired(valuePassed?: string): boolean {
    try {
      if (valuePassed?.length === 0) {
        return true;
      }
      const value = valuePassed ?? '';
      const decodedToken: ITokenObject = jwtDecode(value);
      const expiry = new Date(decodedToken.exp * 1000);
      const now = Date.now();
      return expiry.getTime() <= now;
    } catch (e) {
      return true;
    }
  }

  // Roles

  getRole(): Observable<string[]> {
    return fromPromise(localForage.getItem<string[]>('role')).pipe(
      map((data) => {
        return data ?? [];
      })
    );
  }

  setRole(value: string[]) {
    return fromPromise(localForage.setItem('role', value));
  }

  cleanData() {
    this.setRole([]);
    this.setToken('');
    this.setPos(false);
    this.setUsername('');
    this.setClient('');
    this.setCobranza(false);
    this._redirectionUrl.set(null);
  }

  // Pos

  getPos(): Observable<boolean> {
    // return this._pos();
    return fromPromise(localForage.getItem<boolean>('pos')).pipe(map((data): boolean => !!data));
  }

  setPos(value: boolean) {
    return fromPromise(localForage.setItem<boolean>('pos', value));
  }

  // Cobranza

  getCobranza() {
    return fromPromise(localForage.getItem<boolean>('cobranza'));
  }

  setCobranza(value: boolean) {
    return fromPromise(localForage.setItem<boolean>('cobranza', value));
  }

  // Username

  getUsername(): Observable<string | null> {
    return fromPromise(localForage.getItem<string>('username'));
  }

  setUsername(value: string) {
    return fromPromise(localForage.setItem<string>('username', value));
  }

  getClient() {
    return fromPromise(localForage.getItem<string>('client'));
  }

  setClient(value: string) {
    return fromPromise(localForage.setItem<string>('client', value));
  }

  /**
   * Hidrata la sesión a partir del paquete de handoff que envía el dashboard v1
   * (query param `hp_session`). Reutiliza el mismo storage que un login normal,
   * pero los datos vienen explícitos de v1 en vez de la respuesta del backend.
   * Devuelve `false` sin tocar el storage si el paquete es inválido o el token
   * ya expiró; en ese caso el flujo normal (guard → login) decide.
   */
  hydrateFromHandoff(payload: IHandoffSession): Observable<boolean> {
    if (!this._isValidHandoffPayload(payload) || this.isTokenExpired(payload.token)) {
      return of(false);
    }
    return forkJoin([
      this.setToken(payload.token),
      this.setRole(payload.role),
      this.setUsername(payload.username),
      this.setClient(payload.client),
      this.setPos(false),
      this.setCobranza(false),
    ]).pipe(map(() => true));
  }

  private _isValidHandoffPayload(payload: IHandoffSession): boolean {
    return (
      !!payload &&
      typeof payload.token === 'string' &&
      payload.token.length > 0 &&
      Array.isArray(payload.role) &&
      payload.role.length > 0 &&
      typeof payload.username === 'string' &&
      typeof payload.client === 'string'
    );
  }

  updateUser(response: IPostLoginResponse) {
    const roles = response.user.authorities.map((item) => item.authority);
    this._identifyClarity(response, roles);
    return forkJoin([
      this.setToken(response.token),
      this.setRole(roles),
      this.setUsername(response.user.username),
      this.setClient(response.user.client),
    ]).pipe(map(() => response));
  }

  /**
   * Asocia la sesión de Clarity al usuario logueado para segmentar grabaciones por
   * usuario/cliente/rol. Es no-op si Clarity no está cargado (app de clientes o SSR),
   * y nunca debe romper el login: cualquier error se traga.
   */
  private _identifyClarity(response: IPostLoginResponse, roles: string[]): void {
    const clarity = (globalThis as { clarity?: (...args: unknown[]) => void }).clarity;
    if (typeof clarity !== 'function') {
      return;
    }
    try {
      const username = response.user.username;
      clarity('identify', username);
      clarity('set', 'username', username);
      clarity('set', 'client', response.user.client ?? '');
      clarity('set', 'role', roles.join(','));
    } catch {
      // analytics no debe interrumpir el flujo de login
    }
  }
}
